import { z } from "zod";
import { EXPENSE_STATUS, expenseSpentAtSchema } from "./expenses.js";

/* ===========================================================================
 * US-E7.F3-0051 — Edit / Delete Expense (Reversal) (@UC0036)
 * POST /v1/expenses/:id/reversal | PATCH /v1/expenses/:id | DELETE /v1/expenses/:id
 * Phase 8 Consensus + Phase 6 LEDGER — @gate money,ledger 🔴.
 *
 * reversal (approved/posted) → EXPENSE_REVERSAL append-only qua LedgerService.reverse (móng 0041):
 *   đảo line gốc (DEBIT CASH / CREDIT EXPENSE) → CASH khôi phục + expense→reversed + link.
 *   NEVER hard-delete/UPDATE ledger gốc.
 * PATCH → sửa metadata (name/category/description) + audit. amountMinor immutable → reject 400.
 * DELETE → hard-delete CHỈ khi status=pending_decision (chưa ledger); posted/reversed → reject.
 * ========================================================================= */

/** amountMinor immutable — sửa tiền = huỷ + tạo lại (UC0036). Message client + server dùng chung. */
export const EXPENSE_AMOUNT_IMMUTABLE_MESSAGE =
  "Để thay đổi số tiền, huỷ và tạo lại khoản chi.";

/**
 * 🔴 US-E24.F52-0504 (@UC0036 AC13) — MÃ LỖI ỔN ĐỊNH khi xoá khoản chi ĐÃ ghi sổ.
 *
 * Luật không đổi (posted ⇒ không xoá cứng, ledger append-only), chỉ có **cách nói** đổi: trước đây
 * client chỉ nhận `code: "CONFLICT"` chung ⇒ hiện được mỗi câu lỗi chung chung, trong khi ĐÚNG việc
 * người dùng cần làm tiếp là **huỷ bằng bút toán đảo** (`POST /v1/expenses/:id/reversal`). Mã này
 * là thứ client rẽ nhánh, không phải `detail` (§7: `detail` chỉ để debug).
 *
 * 🔴 Ngôn-ngữ-trung-tính: backend KHÔNG trả câu đã dịch; client map `code → message`.
 */
export const EXPENSE_POSTED_REVERSAL_REQUIRED_CODE =
  "EXPENSE_POSTED_REVERSAL_REQUIRED" as const;

/* ---- PATCH metadata only ---- */
/**
 * Body PATCH: name/category/description + `spentAt` (nhãn NGÀY). `.strict()` → field lạ (kể cả
 * `amountMinor`) → reject 400 (amountMinor immutable — B1). Mọi field optional nhưng phải có ≥1
 * (refine) — tránh PATCH rỗng.
 *
 * 🔴 `spentAt` KHÔNG phải tiền và KHÔNG mở cửa cho tiền: nó là nhãn hiển thị, không tham gia tính
 * số dư/kỳ báo cáo/thứ tự cursor. Sửa SỐ TIỀN vẫn đi **đúng một đường**
 * `POST /v1/funds/:id/ledger-corrections` (REVERSAL + POST trong một transaction).
 * `null` = xoá ngày về *"không ai khai"* — **khác** *"chi hôm nay"*, nên không lấp bằng `now()`.
 */
export const patchExpenseRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    category: z.string().trim().max(100).nullable().optional(),
    description: z.string().trim().max(1000).nullable().optional(),
    // Luật "không ở tương lai" dùng CHUNG với lúc tạo (`expenseSpentAtSchema`) — không chép lại.
    spentAt: expenseSpentAtSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.name !== undefined ||
      v.category !== undefined ||
      v.description !== undefined ||
      v.spentAt !== undefined,
    "Không có thay đổi nào để lưu.",
  );

export type PatchExpenseRequest = z.infer<typeof patchExpenseRequestSchema>;

export const patchExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    name: z.string().nullable(),
    category: z.string().nullable(),
    description: z.string().nullable(),
    /** Nhãn ngày sau khi sửa — ghi rồi phải đọc lại được NGAY, không đợi GET thứ hai. */
    spentAt: z.string().datetime().nullable(),
    status: z.string(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export type PatchExpenseResponse = z.infer<typeof patchExpenseResponseSchema>;

/* ---- reversal → EXPENSE_REVERSAL append-only ---- */
export const reverseExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    /** tx REVERSAL mới (đảo line gốc). */
    reversalTxId: z.string().uuid(),
    /** tx EXPENSE gốc bị đảo (link, vẫn còn nguyên — append-only). */
    reversesTxId: z.string().uuid(),
    newStatus: z.literal(EXPENSE_STATUS.REVERSED),
    reversedAt: z.string().datetime(),
  })
  .strict();

export type ReverseExpenseResponse = z.infer<
  typeof reverseExpenseResponseSchema
>;

/* ---- hard-delete (pending_decision only) ---- */
export const deleteExpenseResponseSchema = z
  .object({
    expenseId: z.string().uuid(),
    deleted: z.literal(true),
  })
  .strict();

export type DeleteExpenseResponse = z.infer<
  typeof deleteExpenseResponseSchema
>;
